import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { confirmAsync } from '../utils/notify';
import './InstagramConnection.css';

/**
 * Instagram Connection component.
 *
 * Uses Facebook Login for Business in a popup window. The resulting
 * Page Access Token is required by the IG Reels Resumable Upload API
 * and is persisted on the InstagramAccount row.
 *
 * Flow:
 * 1. Frontend opens the Facebook OAuth URL (returned by the backend)
 *    in a popup
 * 2. User authorizes on Facebook
 * 3. Facebook redirects to /api/v1/social/instagram/oauth/callback-fb
 *    with the authorization code
 * 4. Backend exchanges code → long-lived FB user token → page token,
 *    discovers the IG Business account, saves to DB
 * 5. Backend returns HTML that sends postMessage to parent window
 * 6. Frontend receives message and refreshes account status
 */
function InstagramConnection() {
  const { t } = useTranslation();
  const popupRef = useRef(null);
  const popupCheckIntervalRef = useRef(null);

  const [connectedAccount, setConnectedAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch connected account status
  const fetchAccountStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/v1/social/instagram/account');
      setConnectedAccount(response.data);
    } catch (err) {
      console.error('Error fetching Instagram account status:', err);
      if (err.response?.status !== 401 && err.response?.status !== 404) {
        setError(t('instagram.errorFetching'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAccountStatus();
  }, [fetchAccountStatus]);

  // Listen for OAuth callback message from popup
  useEffect(() => {
    const handleMessage = (event) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        return;
      }

      const data = event.data;
      if (data?.type === 'INSTAGRAM_OAUTH_CALLBACK') {
        console.log('📸 Received OAuth callback message:', data);
        
        // Clear popup check interval
        if (popupCheckIntervalRef.current) {
          clearInterval(popupCheckIntervalRef.current);
          popupCheckIntervalRef.current = null;
        }

        if (data.success) {
          setSuccess(t('instagram.connectSuccess', { username: data.username || 'Instagram' }));
          fetchAccountStatus();
        } else if (data.error) {
          const errorKey = `instagram.errors.${data.error}`;
          setError(t(errorKey) !== errorKey ? t(errorKey) : data.error);
        }
        setConnecting(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [t, fetchAccountStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (popupCheckIntervalRef.current) {
        clearInterval(popupCheckIntervalRef.current);
      }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  // Start OAuth via native Instagram OAuth URL in popup
  const handleConnect = async () => {
    setConnecting(true);
    setError(null);

    try {
      // Get OAuth URL from backend (Facebook Login for Business path).
      // The state parameter (igfb_<user_id>) provides CSRF protection.
      const response = await apiClient.get('/api/v1/social/instagram/oauth/start-fb');
      const { oauth_url } = response.data;

      console.log('📸 Opening Instagram (FB Login for Business) OAuth popup:', oauth_url);

      // Open popup centered on screen
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      popupRef.current = window.open(
        oauth_url,
        'instagram_oauth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      if (!popupRef.current) {
        setError(t('instagram.errors.popupBlocked'));
        setConnecting(false);
        return;
      }

      // Check if popup was closed without completing OAuth
      popupCheckIntervalRef.current = setInterval(() => {
        if (popupRef.current && popupRef.current.closed) {
          clearInterval(popupCheckIntervalRef.current);
          popupCheckIntervalRef.current = null;
          // Only set error if still connecting (didn't receive success message)
          setConnecting((current) => {
            if (current) {
              setError(t('instagram.errors.loginCancelled'));
              return false;
            }
            return current;
          });
        }
      }, 500);

    } catch (err) {
      console.error('Failed to start Instagram OAuth:', err);
      setError(t('instagram.errors.connectionFailed'));
      setConnecting(false);
    }
  };

  // Disconnect account
  const handleDisconnect = async () => {
    if (!(await confirmAsync({ message: t('instagram.confirmDisconnect'), danger: true }))) return;

    setDisconnecting(true);
    setError(null);
    try {
      await apiClient.delete('/api/v1/social/instagram/disconnect');
      setConnectedAccount(null);
      setSuccess(t('instagram.disconnectSuccess'));
    } catch (err) {
      const message = err.response?.data?.detail || t('instagram.errorDisconnecting');
      setError(message);
    } finally {
      setDisconnecting(false);
    }
  };

  // Refresh account info and token
  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await apiClient.post('/api/v1/social/instagram/refresh');
      if (response.data.account) {
        setConnectedAccount((prev) => ({
          ...prev,
          ...response.data.account,
        }));
      }
      setSuccess(t('instagram.refreshSuccess'));
    } catch (err) {
      const message = err.response?.data?.detail || t('instagram.errorRefreshing');
      setError(message);
    } finally {
      setRefreshing(false);
    }
  };

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <div className="instagram-connection loading">
        <div className="spinner" />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="instagram-connection">
      <div className="section-header">
        <div className="header-icon">📷</div>
        <div className="header-text">
          <h2>{t('instagram.connectionTitle')}</h2>
          <p className="description">{t('instagram.connectionDescription')}</p>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="alert alert-success">
          <span className="icon">✓</span>
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="dismiss">×</button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="alert alert-error">
          <span className="icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="dismiss">×</button>
        </div>
      )}

      {connectedAccount ? (
        /* Connected Account View */
        <div className="connected-account">
          <div className="account-card">
            <div className="account-info">
              {connectedAccount.profile_picture_url ? (
                <img
                  src={connectedAccount.profile_picture_url}
                  alt={connectedAccount.instagram_username}
                  className="profile-picture"
                />
              ) : (
                <div className="profile-picture placeholder">
                  <span>📷</span>
                </div>
              )}
              <div className="account-details">
                <h3>@{connectedAccount.instagram_username}</h3>
                {connectedAccount.instagram_name && (
                  <p className="display-name">{connectedAccount.instagram_name}</p>
                )}
                <div className="stats">
                  {connectedAccount.followers_count != null && (
                    <span className="stat">
                      <strong>{connectedAccount.followers_count.toLocaleString()}</strong> {t('instagram.followers')}
                    </span>
                  )}
                  {connectedAccount.media_count != null && (
                    <span className="stat">
                      <strong>{connectedAccount.media_count.toLocaleString()}</strong> {t('instagram.posts')}
                    </span>
                  )}
                </div>
                <p className="connected-date">
                  {t('instagram.connectedOn', {
                    date: new Date(connectedAccount.connected_at).toLocaleDateString(),
                  })}
                </p>
                {connectedAccount.last_post_at && (
                  <p className="last-post">
                    {t('instagram.lastPost', {
                      date: new Date(connectedAccount.last_post_at).toLocaleDateString(),
                    })}
                  </p>
                )}
              </div>
              <div className={`status-badge ${connectedAccount.is_active && connectedAccount.token_valid ? 'active' : 'inactive'}`}>
                {connectedAccount.is_active && connectedAccount.token_valid
                  ? t('instagram.active')
                  : t('instagram.inactive')}
              </div>
            </div>

            {!connectedAccount.token_valid && (
              <div className="token-warning">
                <span className="icon">⚠️</span>
                <span>{t('instagram.tokenExpired')}</span>
              </div>
            )}

            <div className="account-actions">
              <button
                className="btn btn-secondary"
                onClick={handleRefresh}
                disabled={refreshing}
                title={t('instagram.refreshInfo')}
              >
                {refreshing ? '...' : '🔄'} {t('instagram.refresh')}
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                {disconnecting ? t('common.processing') : t('instagram.disconnect')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* No Account Connected */
        <div className="no-account">
          <div className="connect-prompt">
            <div className="icon">📱</div>
            <h3>{t('instagram.noAccountConnected')}</h3>
            <p>{t('instagram.connectPrompt')}</p>
            <ul className="requirements">
              <li>{t('instagram.requirement1')}</li>
              <li>{t('instagram.requirement2')}</li>
              <li>{t('instagram.requirement3')}</li>
            </ul>
          </div>

          <button
            className="btn btn-primary btn-large"
            onClick={handleConnect}
            disabled={connecting}
          >
            {connecting ? (
              <>
                <span className="spinner-small" />
                {t('instagram.connecting')}
              </>
            ) : (
              <>
                📷 {t('instagram.connectViaFacebookCta')}
              </>
            )}
          </button>

        </div>
      )}
    </div>
  );
}

export default InstagramConnection;
